import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { ThemeEvents } from '@theme/events';
import { onDocumentLoaded } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLButtonElement} [trigger] - Optional trigger button (mobile menu)
 * @property {HTMLElement} [content] - Optional menu content container
 */

/**
 * A custom element that manages the site header with mobile menu functionality.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  /**
   * Whether the mobile menu is currently open
   * @type {boolean}
   */
  #isOpen = false;

  /**
   * The trigger button element
   * @type {HTMLButtonElement | null}
   */
  #trigger = null;

  /**
   * The content container element
   * @type {HTMLElement | null}
   */
  #content = null;

  /**
   * The top text span element for navigation buttons
   * @type {HTMLElement | null}
   */
  #textTopSpan = null;

  /**
   * The bottom text span element for navigation buttons
   * @type {HTMLElement | null}
   */
  #textBottomSpan = null;

  #handleStickyHeadingChange = () => {
    // Don't run on mobile
    if (!window.matchMedia('(min-width: 990px)').matches) {
      this.classList.remove('header--has-background');
      return;
    }

    // Check if any sticky headings are present
    const hasSticky = document.querySelectorAll('[data-sticky-heading="true"]').length > 0;

    // Add or remove the background class based on the number of sticky headings present
    if (hasSticky) {
      this.classList.add('header--has-background');
    } else {
      this.classList.remove('header--has-background');
    }
  };

  /**
   * Handles clicks outside the menu to close it
   * @param {MouseEvent} e
   */
  #handleClickOutside = (e) => {
    if (!e.target || !(e.target instanceof Node)) return;
    if (this.#isOpen && !this.contains(e.target)) {
      this.#close();
    }
  };

  /**
   * Sets up accessibility attributes for the mobile menu
   */
  #setupAccessibility = () => {
    if (!this.#content || !this.#trigger) return;
    // Use existing ID from HTML
    const contentId = this.#content.id;

    // Set up ARIA attributes for trigger
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#trigger.setAttribute('aria-controls', contentId);
    this.#trigger.setAttribute('aria-haspopup', 'dialog');

    // Content setup is handled by semantic HTML
    this.#content.setAttribute('data-expanded', 'false');
  };

  /**
   * Binds event listeners for the mobile menu
   */
  #bindEvents = () => {
    if (!this.#trigger || !this.#content) return;
    // Click handler
    this.#trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.#toggle();
    });

    // Keyboard handler for trigger
    this.#trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#toggle();
      }
      if (e.key === 'Escape' && this.#isOpen) {
        this.#close();
      }
    });

    // Escape key handler for content
    this.#content.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#close();
      }
    });
  };

  /**
   * Toggles the mobile menu open/closed state
   */
  #toggle = () => {
    if (this.#isOpen) {
      this.#close();
    } else {
      this.#open();
    }
  };

  /**
   * Opens the mobile menu
   */
  #open = () => {
    if (!this.#trigger || !this.#content) return;
    if (this.#isOpen) return;

    this.#isOpen = true;

    // Apply scroll lock
    this.#applyScrollLock();

    // CSS handles the animation via data attribute
    this.setAttribute('data-expanded', '');

    // Update ARIA states
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#content.setAttribute('aria-hidden', 'false');
    this.#content.removeAttribute('inert');
    this.#content.setAttribute('data-expanded', 'true');

    // Update trigger text spans for navigation buttons
    this.#updateTriggerText('Close');

    // Enable focus trap for modal dialog accessibility
    trapFocus(this.#content);

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('mobile-menu:opened'));
  };

  /**
   * Closes the mobile menu
   */
  #close = () => {
    if (!this.#isOpen || !this.#trigger || !this.#content) return;

    this.#isOpen = false;

    // Remove scroll lock
    this.#removeScrollLock();

    // Disable focus trap before closing
    removeTrapFocus();

    // CSS handles the animation via data attribute
    this.removeAttribute('data-expanded');

    // Update ARIA states
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#content.setAttribute('aria-hidden', 'true');
    this.#content.setAttribute('inert', '');
    this.#content.setAttribute('data-expanded', 'false');

    // Update trigger text spans for navigation buttons
    this.#updateTriggerText('Menu');

    // Return focus to trigger
    this.#trigger.focus();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('mobile-menu:closed'));
  };

  /**
   * Updates the trigger button text
   * @param {string} text - The text to display
   */
  #updateTriggerText = (text) => {
    if (!this.#trigger) return;
    // If we found the navigation button spans, update them
    if (this.#textTopSpan && this.#textBottomSpan) {
      this.#textTopSpan.textContent = text;
      this.#textBottomSpan.textContent = text;
    } else {
      // Fallback to updating the entire button text for non-navigation buttons
      this.#trigger.textContent = text;
    }
  };

  /**
   * Applies scroll lock
   */
  #applyScrollLock = () => {
    document.documentElement.setAttribute('scroll-lock', '');
  };

  /**
   * Removes scroll lock
   */
  #removeScrollLock = () => {
    document.documentElement.removeAttribute('scroll-lock');
  };

  connectedCallback() {
    super.connectedCallback();

    // Use this.querySelector since everything is now inside the component
    this.#trigger = this.querySelector(`#${this.dataset.trigger || ''}`);
    this.#content = this.querySelector(`#${this.dataset.target || ''}`);

    if (!this.#trigger || !this.#content) {
      console.warn('HeaderComponent: Missing required elements');
      return;
    }

    // Find the span elements for text updates
    this.#textTopSpan = this.#trigger.querySelector('.button__text-top');
    this.#textBottomSpan = this.#trigger.querySelector('.button__text-bottom');

    this.#setupAccessibility();
    this.#bindEvents();

    // Handler for clicks outside the menu
    document.addEventListener('click', this.#handleClickOutside);
    document.addEventListener(ThemeEvents.stickyHeadingEntered, this.#handleStickyHeadingChange);
    document.addEventListener(ThemeEvents.stickyHeadingExited, this.#handleStickyHeadingChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Remove handler for clicks outside the menu
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener(ThemeEvents.stickyHeadingEntered, this.#handleStickyHeadingChange);
    document.removeEventListener(ThemeEvents.stickyHeadingExited, this.#handleStickyHeadingChange);

    // Cleanup: ensure scroll is unlocked if component is removed while open
    if (this.#isOpen) {
      this.#removeScrollLock();
      removeTrapFocus();
    }
  }
}

// Register custom element
if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/**
 * A custom element that manages desktop dropdown menu accessibility.
 * Toggles aria-expanded on hover and focus for screen readers.
 *
 * @extends {Component}
 */
class DesktopDropdown extends Component {
  /**
   * The trigger element with aria-controls
   * @type {HTMLElement | null}
   */
  #trigger = null;

  /**
   * Prevents focusin from reopening after Escape
   * @type {boolean}
   */
  #closedByEscape = false;

  /**
   * Sets the expanded state on the trigger
   * @param {boolean} expanded
   */
  #setExpanded = (expanded) => {
    if (!this.#trigger) return;
    this.#trigger.setAttribute('aria-expanded', String(expanded));
  };

  /**
   * Handles mouse enter to expand dropdown
   */
  #handleMouseEnter = () => {
    this.#closedByEscape = false;
    this.#setExpanded(true);
  };

  /**
   * Handles mouse leave to collapse dropdown
   */
  #handleMouseLeave = () => {
    this.#setExpanded(false);
  };

  /**
   * Handles focus entering the dropdown
   */
  #handleFocusIn = () => {
    if (this.#closedByEscape) return;
    this.#setExpanded(true);
  };

  /**
   * Handles focus leaving the dropdown
   * @param {FocusEvent} e
   */
  #handleFocusOut = (e) => {
    // Collapse if focus moves outside the menu item (or leaves document)
    const newFocus = e.relatedTarget;
    if (!newFocus || (newFocus instanceof Node && !this.contains(newFocus))) {
      this.#closedByEscape = false;
      this.#setExpanded(false);
    }
  };

  /**
   * Handles keyboard events for accessibility
   * @param {KeyboardEvent} e
   */
  #handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      this.#closedByEscape = true;
      this.#setExpanded(false);
      this.#trigger?.focus();
    }
  };

  /**
   * Binds event listeners for the dropdown
   */
  #bindEvents = () => {
    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.addEventListener('keydown', this.#handleKeyDown);
  };

  connectedCallback() {
    super.connectedCallback();

    this.#trigger = this.querySelector('[aria-controls]');
    if (!this.#trigger) return;

    this.#bindEvents();
  }
}

// Register custom element
if (!customElements.get('desktop-dropdown')) {
  customElements.define('desktop-dropdown', DesktopDropdown);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header height on resize
  if (header instanceof HTMLElement) {
    const headerResizeObserver = new ResizeObserver(() => {
      document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
    });
    headerResizeObserver.observe(header);
  }

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      const headerGroupHeight = calculateHeaderGroupHeight(header, headerGroup);
      document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
    });

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
